import { SwapMode, } from './common/types';
import { deconstructPairId, fulfilledValues } from './utils';
class ExchangeProviders {
    _allProviders;
    _logger;
    _providers;
    constructor(providers, { logger } = {}) {
        this._allProviders = [...providers];
        this._providers = providers;
        this._logger = logger;
    }
    async getSupportedProvidersForPair(pairId, opts) {
        const supportedPromises = this._providers.map((provider) => provider.isPairSupported(pairId, opts?.[provider.name]));
        const supportedPairsList = await fulfilledValues(supportedPromises, { logger: this._logger });
        const supportedProviders = this._providers.filter((_, index) => supportedPairsList[index]);
        this._logger?.debug(`Supported providers for pair ${pairId}: ${supportedProviders
            .map((provider) => provider.name)
            .join(', ')}`);
        return supportedProviders;
    }
    async getSupportedFromAssetIds() {
        const allFromAssetsIdsFromProviders = await fulfilledValues(this._providers.map((provider) => provider.getSupportedFromAssetIds()), { logger: this._logger });
        return allFromAssetsIdsFromProviders.reduce((allFromAssetsIds, currentFromAssetsIds) => {
            const newFromAssetsIds = new Set(allFromAssetsIds);
            currentFromAssetsIds.forEach((fromAssetId) => newFromAssetsIds.add(fromAssetId));
            return newFromAssetsIds;
        }, new Set());
    }
    async getSupportedToAssetIds(from) {
        const allToAssetsIdsFromProviders = await fulfilledValues(this._providers.map((provider) => provider.getSupportedToAssetIds(from)), { logger: this._logger });
        return allToAssetsIdsFromProviders.reduce((allToAssetsIds, currentToAssetsIds) => {
            const newToAssetsIds = new Set(allToAssetsIds);
            currentToAssetsIds.forEach((toAssetId) => {
                if (toAssetId !== from) {
                    newToAssetsIds.add(toAssetId);
                }
            });
            return newToAssetsIds;
        }, new Set());
    }
    async getQuotes(params) {
        const availableProviders = await this.getSupportedProvidersForPair(params.pairId, params.config);
        const { from } = deconstructPairId(params.pairId);
        const expandFeeOpts = async (quote) => {
            const feeOpts = await params.getFeeOpts({
                amount: params.amount,
                assetId: from,
                fromAddress: params.fromAddress,
                isSendAll: params.isSendAll,
                ...quote.exchangeFeeData.feeOpts,
            });
            const expectedGasLimit = quote.exchangeFeeData.feeOpts?.gasLimit;
            const useEstimatedFees = !expectedGasLimit || feeOpts.gasLimit === expectedGasLimit;
            if (useEstimatedFees) {
                return { ...quote, exchangeFeeData: { ...quote.exchangeFeeData, feeOpts } };
            }
            console.warn('Using legacy ethereum-api when resolving exchange fee opts, please update');
            return quote;
        };
        const quotes = await Promise.all(availableProviders.map((provider) => {
            const partnersConfig = params.config?.[provider.name] || {};
            return provider
                .getQuote({ ...params, ...partnersConfig })
                .then(expandFeeOpts)
                .then((quote) => {
                params.onQuote?.(quote);
                return quote;
            })
                .catch((err) => {
                console.log(err);
                return null;
            });
        }));
        const notNullishQuotes = quotes.filter(Boolean);
        params.onFinish?.(notNullishQuotes);
        return notNullishQuotes;
    }
    async prepareExchange(params) {
        const { provider, ...rest } = params;
        return provider.prepareExchange(rest);
    }
    async executeExchange(params) {
        const { provider, ...rest } = params;
        return provider.executeExchange(rest);
    }
    async getExchangeLimits(params) {
        const supportedProviders = await this.getSupportedProvidersForPair(params.pairId, params.opts);
        const providerLimits = (await fulfilledValues(supportedProviders.map(async (provider) => provider.getLimits(params)), { logger: this._logger }))
            .flat();
        this._logger?.debug(`Limits for pair ${params.pairId}: ${providerLimits
            .map((limit) => JSON.stringify(limit))
            .join(', ')}`);
        if (providerLimits.length === 0) {
            throw new Error(`Couldn't get limits for ${params.pairId}`);
        }
        const bestLimits = providerLimits.reduce((bestLimits, currentLimit) => {
            const newBestLimit = { ...bestLimits };
            if (currentLimit.ExactIn.min.lt(bestLimits.ExactIn.min)) {
                newBestLimit.ExactIn.min = currentLimit.ExactIn.min;
            }
            if (currentLimit.ExactIn.max.gt(bestLimits.ExactIn.max)) {
                newBestLimit.ExactIn.max = currentLimit.ExactIn.max;
            }
            if (currentLimit.ExactOut.min.lt(bestLimits.ExactOut.min)) {
                newBestLimit.ExactOut.min = currentLimit.ExactOut.min;
            }
            if (currentLimit.ExactOut.max.gt(bestLimits.ExactOut.max)) {
                newBestLimit.ExactOut.max = currentLimit.ExactOut.max;
            }
            return newBestLimit;
        }, {
            [SwapMode.ExactIn]: {
                min: providerLimits[0]?.ExactIn.min,
                max: providerLimits[0]?.ExactIn.max,
            },
            [SwapMode.ExactOut]: {
                min: providerLimits[0]?.ExactOut.min,
                max: providerLimits[0]?.ExactOut.max,
            },
        });
        this._logger?.debug(`Best limits for pair ${params.pairId}: ${JSON.stringify(bestLimits)}`);
        return bestLimits;
    }
    getProvider(nameOrProviderClass) {
        if (typeof nameOrProviderClass === 'string') {
            return this._providers.find((provider) => provider.name === nameOrProviderClass);
        }
        if (nameOrProviderClass?.name) {
            return this._providers.find((provider) => provider instanceof nameOrProviderClass);
        }
        return undefined;
    }
    setGeolocation(geolocation) {
        this._providers.forEach((provider) => provider.setGeolocation(geolocation));
    }
    enableProvider(providerName) {
        const provider = this._allProviders.find((provider) => provider.name === providerName);
        if (!provider) {
            this._logger?.warn(`Provider ${providerName} doesn't exist`);
            return;
        }
        if (this._providers.some((provider) => provider.name === providerName)) {
            this._logger?.warn(`Provider ${providerName} is already enabled`);
            return;
        }
        this._providers.push(provider);
    }
    disableProvider(providerName) {
        const index = this._providers.findIndex((provider) => provider.name === providerName);
        if (index === -1) {
            this._logger?.warn(`Provider ${providerName} is already disabled`);
            return;
        }
        this._providers.splice(index, 1);
    }
    async isAssetExchangeable(assetId) {
        const supportedFromAssetIds = await this.getSupportedFromAssetIds();
        if (!supportedFromAssetIds.has(assetId)) {
            return false;
        }
        const supportedToAssetIds = await this.getSupportedToAssetIds(assetId);
        return supportedToAssetIds.size > 0;
    }
}
export { default as Providers } from './providers';
export { default as AssetsService } from './services/assets';
export { default as OrdersService } from './services/orders';
export { default as PricingService } from './services/pricing';
export { SwapMode, } from './common/types';
export { ExchangeError } from './errors';
export default ExchangeProviders;
